<!-- Analytical toolbar: expand/collapse-all, export, print, saved view —
     plus Save/Discard once any editable measure cell has a pending change. -->
<div class="dd-toolbar" *ngIf="showToolbar || hasEdits">
  <button *ngIf="enableExpandCollapseAll" type="button" class="dd-btn" (click)="setAllExpanded(true)" title="Expand all rows">⤢ Expand all</button>
  <button *ngIf="enableExpandCollapseAll" type="button" class="dd-btn" (click)="setAllExpanded(false)" title="Collapse all rows">⤡ Collapse all</button>
  <span class="dd-toolbar-spacer"></span>
  <span class="dd-edit-summary" *ngIf="hasEdits">{{ editedCount }} unsaved change{{ editedCount === 1 ? '' : 's' }}</span>
  <button *ngIf="hasEdits" type="button" class="dd-btn dd-btn-primary" (click)="saveEdits()" title="Save the edited values">✓ Save changes</button>
  <button *ngIf="hasEdits" type="button" class="dd-btn" (click)="discardEdits()" title="Discard the edited values">✕ Discard</button>
  <button *ngIf="enableSavedViews" type="button" class="dd-btn" (click)="saveView()" title="Save current expand/sort view">☆ Save view</button>
  <div class="dd-export-menu" *ngIf="enableExport || enableExcelExport || enablePdfExport || enablePrint">
    <details>
      <summary title="Export / print">⭳ Export</summary>
      <div class="dd-export-list">
        <button *ngIf="enableExport" type="button" class="dd-btn" (click)="exportCsv()">CSV</button>
        <button *ngIf="enableExcelExport" type="button" class="dd-btn" (click)="exportExcel()">Excel</button>
        <button *ngIf="enablePdfExport" type="button" class="dd-btn" (click)="exportPdf()">PDF</button>
        <button *ngIf="enablePrint" type="button" class="dd-btn" (click)="printTable()">Print</button>
      </div>
    </details>
  </div>
</div>

<!-- Two-tier header for pivoted measures (merged drill-down + pivot): a band
     with the measure name ("Quantity") spanning its per-column-value
     sub-headers ("2024 | 2025 | 2026"). Cell widths mirror the measured header
     cells; horizontal scroll is synced with the body, like the Total row. -->
<div class="dd-group-band" #groupBand *ngIf="hasHeaderGroups">
  <div
    *ngFor="let band of groupBands"
    class="dd-group-cell"
    [class.is-group]="!!band.label"
    [ngStyle]="bandCellStyle(band)"
    [title]="band.label"
  >{{ band.label }}</div>
</div>

<!-- <div class="table-wrapper" style="overflow-x: auto"> -->
<ngx-datatable
  #table
  [style.width]="data?.tableWidth || null"
  class="material"
  [ngClass]="freezeRowsClass"
  [rows]="flattenedRows"
  [columns]="visibleColumns"
  [columnMode]="data?.columnMode || 'standard'"
  [headerHeight]="headerHeight"
  [footerHeight]="0"
  [rowHeight]="40"
  (activate)="onCellActivate($event)"
  [externalSorting]="true"
  (sort)="onSort($event)"
  (resize)="onColumnResize()"
  (scroll)="onTableScroll($event)"
  [scrollbarH]="true"
  [scrollbarV]="true"
  [virtualization]="false"
  [rowClass]="rowClass"
>
  <!-- <ngx-datatable-footer>
      <ng-template ngx-datatable-footer-template>
        <div class="footer-total-row">
          <div
            *ngFor="let col of visibleColumns"
            class="footer-cell"
            [style.width.px]="col.width"
          >
            <strong>
              {{
              col.prop === 'name' && col.levelIndex===0
              ? 'Total'
              : (col.prop && col.prop !== 'name' && grandTotalRow?.[col.prop] !== undefined && grandTotalRow?.[col.prop] !== null
              ? (col.type=='number' ? formatNumberCell(grandTotalRow[col.prop]) : (grandTotalRow[col.prop] | number : "1.1-1") +'%')
              : '')
              }}
            </strong>
          </div>
        </div>
      </ng-template>
    </ngx-datatable-footer> -->

<div class="table-spinner-overlay" *ngIf="isLoading">
  <mat-spinner diameter="40"></mat-spinner>
</div>
<ng-template #percentageCell let-value="value">
  {{ value | number : "1.1-1" }}%
</ng-template>
<ng-template #numberCell let-value="value" let-column="column">
  {{ formatNumberCell(value, column) }}
</ng-template>

<!-- Editable measure cell: numeric measures edit on LEAF rows only (parents
     are re-summed aggregates); TEXT attribute columns edit on every level,
     top-level parents included. Committed on change/blur, never per keystroke
     (the post-commit re-flatten would drop focus). Total rows stay read-only.

     Click-to-edit applies to the DROPDOWN editor ONLY: an already-answered
     dropdown shows its value as plain text, same as a read-only cell, and the
     <select> only appears once clicked — otherwise a row full of answered
     dropdowns looked like an open, half-filled form. Free-text/number inputs
     are NOT affected — they stay visible always, same as before. -->
<ng-template #editCell let-value="value" let-row="row" let-column="column">
  <!-- Dropdown editor (header editor 'dropdown'): a select over the configured
       custom values and/or the column's own values; the blank option clears
       the cell. Commits through the same path as the inputs, then closes back
       to plain-text display. -->
  <select
    *ngIf="isRowEditable(row, column.prop) && isDropdownProp(column.prop) && isActiveEditCell(row, column.prop)"
    class="dd-edit-input dd-edit-select"
    [class.dd-edit-text]="column.type === 'text'"
    [class.dd-edit-dirty]="isCellEdited(row, column.prop)"
    (change)="onCellValueCommit(row, column.prop, $any($event.target).value); closeCellEdit()"
    (blur)="closeCellEdit()"
    (click)="$event.stopPropagation()"
  >
    <option value="" [selected]="value === null || value === undefined || value === ''"></option>
    <option *ngFor="let opt of cellOptions(row, column.prop)" [value]="opt" [selected]="isOptionSelected(opt, value)">{{ opt }}</option>
  </select>
  <!-- Dropdown, not yet clicked: looks like the select it opens (boxed, with
       the same chevron) so every row reads as a dropdown, not just the active
       one — plain text alone didn't say "click me, this is a picker". -->
  <span
    *ngIf="isRowEditable(row, column.prop) && isDropdownProp(column.prop) && !isActiveEditCell(row, column.prop)"
    class="dd-edit-display dd-edit-display-select"
    [class.dd-edit-dirty]="isCellEdited(row, column.prop)"
    [class.dd-edit-empty]="value === null || value === undefined || value === ''"
    (click)="beginCellEdit(row, column.prop, $event)"
  ><span class="dd-edit-display-value">{{ value ?? '' }}</span><span class="dd-edit-display-chevron" aria-hidden="true">▾</span></span>
  <!-- Text attribute columns (header type 'text') take a free-text input;
       numeric measures keep the number input. Always visible — no click-to-edit. -->
  <input
    *ngIf="isRowEditable(row, column.prop) && !isDropdownProp(column.prop)"
    [type]="column.type === 'text' ? 'text' : 'number'"
    [attr.step]="column.type === 'text' ? null : 'any'"
    class="dd-edit-input"
    [class.dd-edit-text]="column.type === 'text'"
    [class.dd-edit-dirty]="isCellEdited(row, column.prop)"
    [value]="value ?? ''"
    (change)="onCellValueCommit(row, column.prop, $any($event.target).value)"
    (click)="$event.stopPropagation()"
  />
  <span *ngIf="!isRowEditable(row, column.prop)">{{ column.type === 'text' ? (value ?? '') : formatNumberCell(value) }}</span>
</ng-template>

<!-- Rich visual cell: data-bar / progress / heat-map / conditional / kpi /
     badge / trend / sparkline / icon — driven by data.visualRules[prop].kind. -->
<ng-template #visualCell let-value="value" let-row="row" let-column="column">
  <ng-container [ngSwitch]="row.isTotal ? 'plain' : visualKind(column.prop)">
    <!-- Data bar (value-scaled fill behind the number). -->
    <div *ngSwitchCase="'databar'" class="dd-databar-wrap">
      <span class="dd-databar" [style.width.%]="barWidth(column.prop, value)" [style.background]="barColor(column.prop)"></span>
      <span class="dd-databar-val">{{ formatNumberCell(value) }}</span>
    </div>
    <!-- Progress bar (0..100). -->
    <div *ngSwitchCase="'progress'" class="dd-progress">
      <span class="dd-progress-fill" [style.width.%]="barWidth(column.prop, value)" [style.background]="barColor(column.prop)"></span>
      <span class="dd-progress-label">{{ value | number : "1.0-0" }}%</span>
    </div>
    <!-- Heat map (background scaled to column range). -->
    <div *ngSwitchCase="'heatmap'" class="dd-heat" [style.background]="cellBg(column.prop, value)">{{ formatNumberCell(value) }}</div>
    <!-- Conditional formatting (bg/fg by rule). -->
    <div *ngSwitchCase="'conditional'" class="dd-cond" [style.background]="cellBg(column.prop, value)" [style.color]="cellFg(column.prop, value)">{{ formatNumberCell(value) }}</div>
    <!-- KPI indicator / status badge. -->
    <span *ngSwitchCase="'kpi'" class="dd-badge" [ngClass]="badgeFor(column.prop, value)?.cls">{{ badgeFor(column.prop, value)?.text }}</span>
    <span *ngSwitchCase="'badge'" class="dd-badge" [ngClass]="badgeFor(column.prop, value)?.cls">{{ badgeFor(column.prop, value)?.text }}</span>
    <!-- Trend indicator (arrow + value). -->
    <span *ngSwitchCase="'trend'" class="dd-trend" [ngClass]="trendFor(value).cls">{{ trendFor(value).icon }} {{ formatNumberCell(value) }}</span>
    <!-- Sparkline (mini line chart from an array cell). -->
    <svg *ngSwitchCase="'sparkline'" class="dd-spark" width="60" height="18" viewBox="0 0 60 18" preserveAspectRatio="none">
      <polyline [attr.points]="sparkPoints(value)" fill="none" [attr.stroke]="barColor(column.prop)" stroke-width="1.5" />
    </svg>
    <!-- Mini bar chart (from an array cell). -->
    <svg *ngSwitchCase="'minibar'" class="dd-spark" width="60" height="18" viewBox="0 0 60 18">
      <rect *ngFor="let b of sparkBars(value)" [attr.x]="b.x" [attr.y]="18 - b.h" [attr.width]="b.w" [attr.height]="b.h" [attr.fill]="barColor(column.prop)" />
    </svg>
    <!-- Icon / color-coded dot. -->
    <span *ngSwitchCase="'icon'" class="dd-iconcell"><span class="dd-dot" [style.background]="cellBg(column.prop, value) || barColor(column.prop)"></span>{{ formatNumberCell(value) }}</span>
    <!-- Plain number (fallback / total rows). -->
    <ng-container *ngSwitchDefault>{{ formatNumberCell(value) }}</ng-container>
  </ng-container>
</ng-template>
<ng-template
  #treeCell
  let-row="row"
  let-rowIndex="rowIndex"
  let-columnIndex="columnIndex"
  let-value="value"
  let-column="column"
>
  <div
    *ngIf="row.isTotal && (column.indentByLevel || row.level === column.levelIndex + 1)"
    style="font-weight: bold; color: #444"
    [style.padding-left.px]="column.indentByLevel ? (row.level - 1) * 18 : 0"
  >
    {{ row[nameProp] }}
  </div>

  <div
    *ngIf="!row.isTotal && (column.indentByLevel || row.level === column.levelIndex + 1)"
    style="display: flex; align-items: center; justify-content: space-between"
    [style.padding-left.px]="column.indentByLevel ? (row.level - 1) * 18 : 0"
    [ngClass]="{
      'highlight-cell': highlightedCells.includes(rowIndex + '-' + column.prop)
    }"
  >
    <div style="display: flex; align-items: center; gap: 4px; cursor: pointer">
      <mat-icon
        (click)="toggleExpand(row); $event.stopPropagation()"
        *ngIf="row.children?.length"
        style="
          font-size: calc(18px * var(--dvm-font-scale, 1));
          line-height: 1;
          display: flex;
          align-items: center;
        "
      >
        {{ row.expanded ? "remove_circle_outline" : "add_circle_outline" }}
      </mat-icon>

      <!-- Editable-labels level → the node name is a text input (rename,
           committed on change/blur) — or a select when the level's editor is
           a dropdown (custom values / labels already in the table); otherwise
           the classic bold label. -->
      <select
        *ngIf="isLabelDropdown(row)"
        class="dd-edit-input dd-edit-label dd-edit-select"
        [class.dd-edit-dirty]="isCellEdited(row, nameProp)"
        (change)="onLabelCommit(row, $any($event.target).value)"
        (click)="$event.stopPropagation()"
      >
        <option *ngFor="let opt of labelOptions(row)" [value]="opt" [selected]="isOptionSelected(opt, row[nameProp])">{{ opt }}</option>
      </select>
      <input
        *ngIf="isLabelEditable(row) && !isLabelDropdown(row)"
        type="text"
        class="dd-edit-input dd-edit-label"
        [class.dd-edit-dirty]="isCellEdited(row, nameProp)"
        [value]="row[nameProp] ?? ''"
        (change)="onLabelCommit(row, $any($event.target).value)"
        (click)="$event.stopPropagation()"
      />
      <div
        *ngIf="!isLabelEditable(row)"
        class="dd-node-label"
        style="
          font-weight: bold;
          color: #444;
          display: flex;
          align-items: center;
        "
      >
        {{ row[nameProp] }}
      </div>
    </div>

    <mat-icon
      (click)="$event.stopPropagation()"
      *ngIf="hasNavigation(column.levelIndex)"
      [matMenuTriggerFor]="menu"
      >more_vert</mat-icon
    >
  </div>
  <mat-menu #menu="matMenu">
    <ng-container *ngFor="let navItem of data.navigation">
      <button
        mat-menu-item
        *ngIf="column.levelIndex === navItem.level"
        (click)="onNavigate(navItem, row)"
      >
        {{ navItem.name }}
      </button>
    </ng-container>
  </mat-menu>
</ng-template>

<ng-template #infoIconHeader let-column="column">
  <div
    style="display: flex; align-items: center; flex-grow: 1"
    [ngClass]="'justify-' + column.alignment"
  >
    <span [title]="column.name">{{ column.name }}</span>
    <mat-icon
      *ngIf="column.infoIcon"
      [matTooltip]="column.infoText"
      class="info-icon"
      >info_outline</mat-icon
    >
  </div>
</ng-template>
</ngx-datatable>


<!-- Sticky total row -->

<!-- Sticky total row -->
<!-- Hidden (not removed — the ViewChild is static) when the host's Totals
     placement excludes the bottom row (`data.hideTotalRow`). -->
<div class="sticky-total-row" #totalRow [class.dd-total-row-hidden]="data?.hideTotalRow === true">
  <div
    *ngFor="let col of visibleColumns; let i = index"
    class="total-cell"
    [class.total-cell-fixed]="col.prop === nameProp && col.levelIndex === 0"
    [class.total-cell-frozen]="isFrozenColumn(i)"
    [ngStyle]="totalCellStyle(i, col)"
  >
    <strong>
              {{
              col.prop === nameProp && col.levelIndex===0
              ? 'Total'
              : (col.prop && col.prop !== nameProp && grandTotalRow?.[col.prop] !== undefined && grandTotalRow?.[col.prop] !== null
              ? (col.type=='number' ? formatNumberCell(grandTotalRow[col.prop]) : (grandTotalRow[col.prop] | number : "1.1-1") +'%')
              : '')
              }}
            </strong>
  </div>
</div>

<!-- The table body remains the native horizontal scroller (so touchpad/touch
     gestures work), but its visual track is hidden. This synchronized track is
     deliberately rendered after Total. -->
<div
  class="dd-horizontal-scrollbar"
  #horizontalScrollbar
  (scroll)="onHorizontalScrollbarScroll($event)"
  aria-label="Scroll table columns horizontally"
>
  <div class="dd-horizontal-scrollbar-content" [style.width.px]="totalColumnWidth"></div>
</div>

<!-- </div> -->
